/**
 * Theme Store for Corporate Identity System
 *
 * Manages tenant theme loading, caching, and DOM application.
 * Integrates with tenant switching for instant theme updates.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ThemeConfig,
  TenantTheme,
  ThemeResponse,
  ThemeUpdatePayload,
  ThemeCSSVariables,
  DEFAULT_THEME_COLORS,
  DEFAULT_THEME_FONTS,
} from '@/types/theme.types';
import apiClient from '@/services/api';

interface ThemeState {
  // State
  theme: TenantTheme | null;
  config: ThemeConfig | null;
  isLoading: boolean;
  error: string | null;
  lastLoadedTenantId: number | null;

  // Actions
  loadTheme: (tenantId: number, force?: boolean) => Promise<void>;
  updateTheme: (tenantId: number, payload: ThemeUpdatePayload) => Promise<void>;
  clearTheme: () => void;
  applyThemeToDOM: (config: ThemeConfig) => void;
  removeCSSVariables: () => void;

  // Getters
  isThemeLoaded: () => boolean;
  getCSSVariable: (name: string) => string | null;
}

/**
 * Default theme configuration
 */
const getDefaultConfig = (): ThemeConfig => ({
  colors: {
    primary: '#3B82F6',
    secondary: '#8B5CF6',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
    background: '#FFFFFF',
    text: '#1F2937',
    textSecondary: '#6B7280',
  },
  logo: {
    url: null,
    darkUrl: null,
    faviconUrl: null,
    width: null,
    height: null,
    showInHeader: true,
  },
  fonts: {
    heading: 'Inter',
    body: 'Inter',
    mono: 'JetBrains Mono',
  },
  branding: {
    companyName: null,
    tagline: null,
    websiteUrl: null,
    showCompanyName: true,
    showTagline: false,
  },
  print: {
    headerText: null,
    footerText: null,
    includeLogo: true,
  },
  customStyles: {},
  cssVariables: {
    '--color-primary': '#3B82F6',
    '--color-secondary': '#8B5CF6',
    '--color-success': '#10B981',
    '--color-warning': '#F59E0B',
    '--color-error': '#EF4444',
    '--color-info': '#3B82F6',
    '--color-background': '#FFFFFF',
    '--color-text': '#1F2937',
    '--color-text-secondary': '#6B7280',
    '--font-heading': 'Inter',
    '--font-body': 'Inter',
    '--font-mono': 'JetBrains Mono',
  },
});

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      // Initial state
      theme: null,
      config: null,
      isLoading: false,
      error: null,
      lastLoadedTenantId: null,

      /**
       * Load theme from backend API
       */
      loadTheme: async (tenantId: number, force = false) => {
        const { lastLoadedTenantId, config } = get();

        // Skip if already loaded for this tenant (unless forced)
        if (!force && lastLoadedTenantId === tenantId && config) {
          console.log(`[ThemeStore] Theme already loaded for tenant ${tenantId}, skipping`);
          return;
        }

        set({ isLoading: true, error: null });

        try {
          console.log(`[ThemeStore] Loading theme for tenant ${tenantId}...`);

          // Fetch theme from backend
          const response = await apiClient.get<ThemeResponse>(
            `/tenants/${tenantId}/theme`
          );

          const { theme, config: themeConfig } = response.data;

          // Apply theme to DOM
          get().applyThemeToDOM(themeConfig);

          // Update favicon if provided
          if (themeConfig.logo.faviconUrl) {
            const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
            if (link) {
              link.href = themeConfig.logo.faviconUrl;
            }
          }

          set({
            theme,
            config: themeConfig,
            isLoading: false,
            error: null,
            lastLoadedTenantId: tenantId,
          });

          console.log('[ThemeStore] Theme loaded successfully:', theme.id);
        } catch (error) {
          console.error('[ThemeStore] Failed to load theme:', error);

          // Fallback to default theme on error
          const defaultConfig = getDefaultConfig();
          get().applyThemeToDOM(defaultConfig);

          set({
            theme: null,
            config: defaultConfig,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load theme',
            lastLoadedTenantId: null,
          });
        }
      },

      /**
       * Update theme via backend API
       */
      updateTheme: async (tenantId: number, payload: ThemeUpdatePayload) => {
        set({ isLoading: true, error: null });

        try {
          console.log(`[ThemeStore] Updating theme for tenant ${tenantId}...`);

          // Send update to backend
          const response = await apiClient.put<{ theme: TenantTheme; config: ThemeConfig }>(
            `/tenants/${tenantId}/theme`,
            payload
          );

          const { theme, config: themeConfig } = response.data;

          // Apply updated theme to DOM
          get().applyThemeToDOM(themeConfig);

          set({
            theme,
            config: themeConfig,
            isLoading: false,
            error: null,
            lastLoadedTenantId: tenantId,
          });

          console.log('[ThemeStore] Theme updated successfully');
        } catch (error) {
          console.error('[ThemeStore] Failed to update theme:', error);

          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to update theme',
          });

          throw error; // Re-throw for UI error handling
        }
      },

      /**
       * Clear theme and reset to defaults
       */
      clearTheme: () => {
        console.log('[ThemeStore] Clearing theme...');

        // Remove CSS variables from DOM
        get().removeCSSVariables();

        // Apply default theme
        const defaultConfig = getDefaultConfig();
        get().applyThemeToDOM(defaultConfig);

        set({
          theme: null,
          config: defaultConfig,
          isLoading: false,
          error: null,
          lastLoadedTenantId: null,
        });
      },

      /**
       * Apply theme CSS variables to document root
       */
      applyThemeToDOM: (config: ThemeConfig) => {
        console.log('[ThemeStore] Applying theme to DOM...');

        const root = document.documentElement;
        const { cssVariables } = config;

        // Apply all CSS custom properties
        Object.entries(cssVariables).forEach(([property, value]) => {
          root.style.setProperty(property, value);
        });

        // Apply custom CSS if present
        if (config.customStyles.css) {
          let styleEl = document.getElementById('tenant-custom-styles');

          if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = 'tenant-custom-styles';
            document.head.appendChild(styleEl);
          }

          styleEl.textContent = config.customStyles.css;
        }

        // Apply font families to body
        if (config.fonts.body) {
          document.body.style.fontFamily = config.fonts.body;
        }

        console.log('[ThemeStore] Theme applied to DOM');
      },

      /**
       * Remove all theme CSS variables from DOM
       */
      removeCSSVariables: () => {
        const root = document.documentElement;
        const { config } = get();

        if (!config) return;

        // Remove CSS custom properties
        Object.keys(config.cssVariables).forEach((property) => {
          root.style.removeProperty(property);
        });

        // Remove custom CSS
        const styleEl = document.getElementById('tenant-custom-styles');
        if (styleEl) {
          styleEl.remove();
        }

        console.log('[ThemeStore] CSS variables removed from DOM');
      },

      /**
       * Check if theme is loaded
       */
      isThemeLoaded: () => {
        const { config } = get();
        return config !== null;
      },

      /**
       * Get specific CSS variable value
       */
      getCSSVariable: (name: string) => {
        const { config } = get();
        if (!config) return null;

        return config.cssVariables[name] || null;
      },
    }),
    {
      name: 'bonnycloud-theme',
      partialize: (state) => ({
        // Only persist theme config, not loading/error states
        theme: state.theme,
        config: state.config,
        lastLoadedTenantId: state.lastLoadedTenantId,
      }),
    }
  )
);

/**
 * Initialize theme on app startup
 * Call this from App.tsx after authentication
 */
export const initializeTheme = async (tenantId: number): Promise<void> => {
  const store = useThemeStore.getState();
  await store.loadTheme(tenantId);
};

/**
 * Sync theme with tenant switching
 * Call this when tenant changes
 */
export const syncThemeWithTenant = async (tenantId: number): Promise<void> => {
  const store = useThemeStore.getState();
  await store.loadTheme(tenantId, true); // Force reload on tenant switch
};
